<template>
  <div class="admin-page settlement-approval">
    <admin-page-header
      eyebrow="Finans"
      title="Mutabakat"
      description="Toplu network tahsilatını kontrol edip hakediş ve nakit iadeleri conversion tarihlerine göre kesinleştirin."
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          small
          :loading="loading || historyLoading || updatesLoading || cancellationsLoading"
          @click="refreshAll"
        >
          Yenile
        </v-btn>
      </template>
    </admin-page-header>

    <v-alert
      v-if="errorMessage"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>
    <v-alert
      v-if="successMessage"
      dense
      outlined
      type="success"
      class="mb-4"
      dismissible
      @input="successMessage = ''"
    >
      {{ successMessage }}
    </v-alert>

    <v-row>
      <v-col
        v-for="card in summaryCards"
        :key="card.label"
        cols="12"
        sm="6"
        lg="3"
      >
        <v-card
          outlined
          class="admin-dashboard-card"
        >
          <v-card-text>
            <div class="text-caption text--secondary">
              {{ card.label }}
            </div>
            <div class="text-h5 font-weight-bold mt-2">
              {{ card.value }}
            </div>
            <div class="text-caption text--secondary mt-2">
              {{ card.helper }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card
      outlined
      class="admin-table-card mt-4"
    >
      <v-tabs v-model="activeTab">
        <v-tab>Onay bekleyen</v-tab>
        <v-tab>Son onaylar</v-tab>
        <v-tab>Tutar güncellemeleri</v-tab>
        <v-tab>İptaller</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-card-text class="settlement-approval__filters">
            <v-row align="center">
              <v-col
                cols="12"
                md="4"
              >
                <v-text-field
                  v-model.trim="filters.search"
                  dense
                  outlined
                  hide-details
                  clearable
                  label="Kullanıcı, işlem veya Event ID ara"
                  @keyup.enter="fetchQueue"
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-select
                  v-model="filters.networkSource"
                  :items="networkOptions"
                  dense
                  outlined
                  hide-details
                  clearable
                  label="Network"
                ></v-select>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-select
                  v-model="filters.type"
                  :items="typeOptions"
                  dense
                  outlined
                  hide-details
                  clearable
                  label="Kayıt tipi"
                ></v-select>
              </v-col>
              <v-col
                cols="12"
                md="2"
                class="d-flex justify-end"
              >
                <v-btn
                  color="primary"
                  outlined
                  block
                  :loading="loading"
                  @click="fetchQueue"
                >
                  Filtrele
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-alert
            dense
            text
            type="info"
            class="mx-4 mb-3"
          >
            Bu liste kesinleşmiş bakiye değildir. Yalnızca bekleme süresi dolmuş, network durumu APPROVED olan ve admin onayı bekleyen kayıtları gösterir.
          </v-alert>

          <div class="settlement-approval__selection-bar">
            <span>{{ selectedItems.length }} kayıt seçili · {{ formatMoney(selectedAmount) }}</span>
            <v-btn
              color="primary"
              :disabled="!selectedItems.length"
              :loading="previewLoading"
              @click="prepareConfirmation"
            >
              Seçimi Önizle
            </v-btn>
          </div>

          <v-data-table
            v-model="selectedItems"
            :headers="queueHeaders"
            :items="queue.items || []"
            :loading="loading"
            :footer-props="tableFooterProps"
            item-key="id"
            show-select
            class="admin-data-table elevation-0"
          >
            <template v-slot:item.user="{ item }">
              <div>
                <div class="font-weight-bold">
                  {{ item.userName || '-' }}
                </div>
                <div class="text-caption text--secondary">
                  {{ item.email || '-' }}
                </div>
              </div>
            </template>
            <template v-slot:item.type="{ item }">
              <v-chip
                x-small
                label
                :color="item.type === 'CASHBACK' ? 'info' : 'primary'"
                text-color="white"
              >
                {{ typeLabel(item.type) }}
              </v-chip>
            </template>
            <template v-slot:item.transaction="{ item }">
              <div class="settlement-approval__identifier">
                <span><b>İşlem ID:</b> {{ item.transactionId || '-' }}</span>
                <small><b>Etkinlik Kimliği:</b> {{ item.providerEventId || '-' }}</small>
              </div>
            </template>
            <template v-slot:item.amount="{ item }">
              <strong>{{ formatMoney(item.amount) }}</strong>
            </template>
            <template v-slot:item.entryDate="{ item }">
              {{ formatDateTime(item.entryDate) }}
            </template>
            <template v-slot:item.settlementEligibleAt="{ item }">
              {{ formatDateTime(item.settlementEligibleAt) }}
            </template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item>
          <v-data-table
            :headers="historyHeaders"
            :items="history.items || []"
            :loading="historyLoading"
            :footer-props="tableFooterProps"
            class="admin-data-table elevation-0"
          >
            <template v-slot:item.user="{ item }">
              <div>
                <div class="font-weight-bold">
                  {{ item.userName || '-' }}
                </div>
                <div class="text-caption text--secondary">
                  {{ item.email || '-' }}
                </div>
              </div>
            </template>
            <template v-slot:item.type="{ item }">
              {{ typeLabel(item.type) }}
            </template>
            <template v-slot:item.amount="{ item }">
              <strong>{{ formatMoney(item.amount) }}</strong>
            </template>
            <template v-slot:item.approval="{ item }">
              <div>
                <div>{{ item.approvedBy || '-' }}</div>
                <div
                  v-if="item.approvalNote"
                  class="text-caption text--secondary"
                >
                  {{ item.approvalNote }}
                </div>
              </div>
            </template>
            <template v-slot:item.entryDate="{ item }">
              {{ formatDateTime(item.entryDate) }}
            </template>
            <template v-slot:item.approvedAt="{ item }">
              {{ formatDateTime(item.approvedAt) }}
            </template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item>
          <v-alert
            dense
            text
            type="info"
            class="mx-4 mt-4 mb-3"
          >
            Son 200 gerçek tutar güncellemesi ve mutabakat onayından sonra networkte görülen fark gösterilir.
            Admin uyarıları kullanıcı bakiyesini veya bildirimini değiştirmez.
            İlk conversion gözlemleri bu listeye dahil edilmez.
          </v-alert>

          <v-card-text class="settlement-approval__filters">
            <v-row align="center">
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model.trim="updateSearch"
                  dense
                  outlined
                  hide-details
                  clearable
                  label="Kullanıcı, işlem veya Event ID ara"
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                md="6"
                class="text-md-right text-caption text--secondary"
              >
                {{ formatNumber(updates.totalCount) }} güncelleme / admin uyarısı ·
                Artış {{ formatMoney(updates.totalIncreaseAmount) }} ·
                Azalış {{ formatMoney(updates.totalDecreaseAmount) }}
              </v-col>
            </v-row>
          </v-card-text>

          <v-data-table
            :headers="updateHeaders"
            :items="filteredUpdates"
            :loading="updatesLoading"
            :footer-props="tableFooterProps"
            item-key="transitionId"
            class="admin-data-table elevation-0"
          >
            <template v-slot:item.userName="{ item }">
              <div>
                <div class="font-weight-bold">
                  {{ item.userName || '-' }}
                </div>
                <div class="text-caption text--secondary">
                  {{ item.email || '-' }}
                </div>
              </div>
            </template>
            <template v-slot:item.type="{ item }">
              <v-chip
                x-small
                label
                :color="item.type === 'CASHBACK' ? 'info' : 'primary'"
                text-color="white"
              >
                {{ typeLabel(item.type) }}
              </v-chip>
            </template>
            <template v-slot:item.transactionId="{ item }">
              <div class="settlement-approval__identifier">
                <span><b>İşlem ID:</b> {{ item.transactionId || item.uniqueId || '-' }}</span>
                <small><b>Etkinlik Kimliği:</b> {{ item.providerEventId || '-' }}</small>
              </div>
            </template>
            <template v-slot:item.auditType="{ item }">
              <div>
                <v-chip
                  x-small
                  label
                  :color="updateReasonColor(item.reason)"
                  text-color="white"
                >
                  {{ updateReasonLabel(item.reason) }}
                </v-chip>
                <div
                  v-if="item.approvalNote"
                  class="text-caption text--secondary mt-1"
                >
                  {{ item.approvalNote }}
                </div>
                <div
                  v-if="item.reason === 'ADMIN_APPROVED_NETWORK_CHANGED'"
                  class="text-caption text--secondary"
                >
                  Network durumu: {{ item.observedStatusRaw || item.observedStatus || '-' }}
                </div>
              </div>
            </template>
            <template v-slot:item.amountTransition="{ item }">
              <strong>{{ formatMoney(item.oldAmount) }} → {{ formatMoney(item.newAmount) }}</strong>
            </template>
            <template v-slot:item.amountDelta="{ item }">
              <v-chip
                x-small
                outlined
                :color="amountDeltaColor(item.amountDelta)"
              >
                {{ formatSignedMoney(item.amountDelta) }}
              </v-chip>
            </template>
            <template v-slot:item.statusTransition="{ item }">
              {{ statusLabel(item.fromStatus) }} → {{ statusLabel(item.toStatus) }}
            </template>
            <template v-slot:item.updatedAt="{ item }">
              {{ formatDateTime(item.updatedAt) }}
            </template>
          </v-data-table>
        </v-tab-item>
        <v-tab-item>
          <v-alert
            dense
            text
            type="info"
            class="mx-4 mt-4 mb-3"
          >
            İptal durumuna geçen hakediş ve nakit iade kayıtlarıdır. Sonradan geri açılan işlemlerin iptal geçmişi korunur.
            Sıfır tutar nedeniyle gizlenen veya hatalı olduğu doğrulanarak geri alınan geçişler dahil edilmez.
          </v-alert>
          <v-card-text>
            <v-text-field
              v-model.trim="cancellationSearch"
              outlined
              dense
              clearable
              hide-details
              label="Kullanıcı, e-posta veya işlem ID ara"
              @keyup.enter="searchCancellations"
              @click:clear="clearCancellationSearch"
            ></v-text-field>
            <div class="d-flex align-center justify-space-between mt-3">
              <span>{{ formatNumber(cancellations.totalElements) }} iptal kaydı</span>
              <v-btn
                outlined
                small
                color="primary"
                :loading="cancellationsLoading"
                @click="searchCancellations"
              >
                Ara
              </v-btn>
            </div>
            <v-alert
              v-if="cancellationsError"
              dense
              text
              type="error"
              class="mt-3"
            >
              {{ cancellationsError }}
              <v-btn
                text
                small
                @click="fetchCancellations"
              >
                Tekrar dene
              </v-btn>
            </v-alert>
          </v-card-text>
          <v-data-table
            :headers="cancellationHeaders"
            :items="cancellations.content || []"
            :server-items-length="cancellations.totalElements || 0"
            :options="cancellationOptions"
            :loading="cancellationsLoading"
            :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
            item-key="transitionId"
            no-data-text="İptal kaydı bulunamadı."
            class="admin-data-table elevation-0"
            @update:options="updateCancellationOptions"
          >
            <template v-slot:item.userName="{ item }">
              <strong>{{ item.userName || '-' }}</strong>
              <div class="text-caption text--secondary">
                {{ item.email || '-' }}
              </div>
            </template>
            <template v-slot:item.type="{ item }">
              {{ typeLabel(item.type) }}
            </template>
            <template v-slot:item.transactionId="{ item }">
              <div class="settlement-approval__identifier">
                <span><b>İşlem ID:</b> {{ item.transactionId || item.uniqueId || '-' }}</span>
                <small><b>Etkinlik Kimliği:</b> {{ item.providerEventId || '-' }}</small>
              </div>
            </template>
            <template v-slot:item.oldAmount="{ item }">
              {{ formatMoney(item.oldAmount) }}
            </template>
            <template v-slot:item.reason="{ item }">
              {{ cancellationReasonLabel(item.reason) }}
              <div class="text-caption text--secondary">
                {{ item.observedStatusRaw || item.observedStatus || '-' }}
              </div>
              <div
                v-if="item.approvalNote"
                class="text-caption"
              >
                {{ item.approvalNote }}
              </div>
            </template>
            <template v-slot:item.statusTransition="{ item }">
              {{ statusLabel(item.fromStatus) }} → {{ statusLabel(item.toStatus) }}
            </template>
            <template v-slot:item.updatedAt="{ item }">
              {{ formatDateTime(item.updatedAt) }}
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog
      v-model="confirmDialog"
      persistent
      max-width="640"
    >
      <v-card class="admin-dialog-card">
        <v-card-title>Mutabakatı kesinleştir</v-card-title>
        <v-card-text v-if="previewPlan">
          <v-alert
            dense
            text
            type="warning"
          >
            Bu işlem {{ previewPlan.totalCount }} kaydı {{ formatMoney(previewPlan.totalAmount) }} kesinleşmiş bakiyeye taşıyacak ve kullanıcı bildirimlerini oluşturacaktır.
          </v-alert>
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model.trim="approvalNote"
                outlined
                dense
                rows="2"
                counter="500"
                label="Not (opsiyonel)"
              ></v-textarea>
            </v-col>
          </v-row>
          <dl class="settlement-approval__preview-facts">
            <div>
              <dt>Hakediş</dt>
              <dd>{{ formatMoney(previewPlan.earningAmount) }}</dd>
            </div>
            <div>
              <dt>Nakit iade</dt>
              <dd>{{ formatMoney(previewPlan.cashbackAmount) }}</dd>
            </div>
            <div>
              <dt>Conversion tarih aralığı</dt>
              <dd>{{ conversionDateRange(previewPlan.items) }}</dd>
            </div>
            <div>
              <dt>Plan özeti</dt>
              <dd class="settlement-approval__hash">
                {{ previewPlan.planHash }}
              </dd>
            </div>
          </dl>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="confirmLoading"
            @click="closeConfirmation"
          >
            Vazgeç
          </v-btn>
          <v-btn
            color="primary"
            :loading="confirmLoading"
            @click="confirmSettlement"
          >
            Seçilenleri Kesinleştir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import { getAccessToken } from '@/utils/panelSession'

export default {
  name: 'SettlementApproval',
  components: {
    AdminPageHeader,
  },
  data: () => ({
    activeTab: 0,
    loading: false,
    historyLoading: false,
    updatesLoading: false,
    cancellationsLoading: false,
    cancellationsError: '',
    cancellationSearch: '',
    appliedCancellationSearch: '',
    cancellationRequestId: 0,
    cancellationOptions: { page: 1, itemsPerPage: 25 },
    cancellations: { content: [], totalElements: 0 },
    previewLoading: false,
    confirmLoading: false,
    confirmDialog: false,
    errorMessage: '',
    successMessage: '',
    approvalNote: '',
    previewPlan: null,
    selectedItems: [],
    updateSearch: '',
    filters: {
      search: '',
      networkSource: null,
      type: null,
    },
    queue: {
      totalCount: 0,
      totalAmount: 0,
      earningAmount: 0,
      cashbackAmount: 0,
      items: [],
    },
    history: {
      totalCount: 0,
      totalAmount: 0,
      items: [],
    },
    updates: {
      totalCount: 0,
      totalIncreaseAmount: 0,
      totalDecreaseAmount: 0,
      items: [],
    },
    networkOptions: [
      { text: 'GelirOrtakları', value: 'GELIRORTAKLARI' },
      { text: 'ReklamAction', value: 'REKLAMACTION' },
    ],
    typeOptions: [
      { text: 'Hakediş', value: 'EARNING' },
      { text: 'Nakit iade', value: 'CASHBACK' },
    ],
    queueHeaders: [
      { text: 'Kullanıcı', value: 'user', width: 230 },
      { text: 'Tip', value: 'type', width: 105 },
      { text: 'Network', value: 'networkSource', width: 150 },
      { text: 'Link adı', value: 'linkName', width: 190 },
      {
        text: 'İşlem / Event ID', value: 'transaction', sortable: false, width: 220,
      },
      {
        text: 'Tutar', value: 'amount', align: 'end', width: 120,
      },
      { text: 'Conversion tarihi', value: 'entryDate', width: 170 },
      { text: 'Onaya uygun tarih', value: 'settlementEligibleAt', width: 170 },
    ],
    historyHeaders: [
      { text: 'Kullanıcı', value: 'user', width: 230 },
      { text: 'Tip', value: 'type', width: 105 },
      { text: 'Network', value: 'networkSource', width: 150 },
      {
        text: 'Tutar', value: 'amount', align: 'end', width: 120,
      },
      { text: 'Conversion tarihi', value: 'entryDate', width: 170 },
      {
        text: 'Onaylayan / Not', value: 'approval', sortable: false, width: 260,
      },
      { text: 'Kesinleşme', value: 'approvedAt', width: 170 },
    ],
    updateHeaders: [
      { text: 'Kullanıcı', value: 'userName', width: 230 },
      { text: 'Tip', value: 'type', width: 105 },
      { text: 'Network', value: 'networkSource', width: 150 },
      { text: 'Link adı', value: 'linkName', width: 190 },
      {
        text: 'İşlem / Event ID', value: 'transactionId', width: 220,
      },
      {
        text: 'Kayıt / Detay', value: 'auditType', sortable: false, width: 330,
      },
      {
        text: 'Tutar değişimi', value: 'amountTransition', sortable: false, width: 220,
      },
      {
        text: 'Fark', value: 'amountDelta', align: 'end', width: 110,
      },
      {
        text: 'Durum', value: 'statusTransition', sortable: false, width: 170,
      },
      { text: 'Güncelleme', value: 'updatedAt', width: 170 },
    ],
  }),
  computed: {
    cancellationHeaders() {
      return [
        { text: 'Kullanıcı', value: 'userName', width: 220 },
        { text: 'Tip', value: 'type', width: 100 },
        { text: 'Network', value: 'networkSource', width: 150 },
        { text: 'Link adı', value: 'linkName', width: 190 },
        { text: 'İşlem / Event ID', value: 'transactionId', width: 230 },
        {
          text: 'İptal öncesi tutar', value: 'oldAmount', align: 'end', width: 150,
        },
        { text: 'İptal nedeni', value: 'reason', width: 230 },
        { text: 'Durum', value: 'statusTransition', width: 170 },
        { text: 'İptal tarihi', value: 'updatedAt', width: 170 },
      ].map(header => ({ ...header, sortable: false }))
    },
    requestOptions() {
      return {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      }
    },
    tableFooterProps() {
      return {
        'items-per-page-options': [25, 50, 100, 200],
      }
    },
    selectedAmount() {
      return this.selectedItems.reduce((sum, item) => sum + Number(item.amount || 0), 0)
    },
    filteredUpdates() {
      const items = this.updates.items || []
      const search = String(this.updateSearch || '').trim().toLocaleLowerCase('tr-TR')
      if (!search) return items

      return items.filter(item => [
        item.userName,
        item.email,
        item.networkSource,
        item.linkName,
        item.uniqueId,
        item.transactionId,
        item.providerEventId,
        item.reason,
        item.approvalNote,
        item.observedStatus,
        item.observedStatusRaw,
      ].some(value => String(value || '').toLocaleLowerCase('tr-TR').includes(search)))
    },
    summaryCards() {
      return [
        {
          label: 'Onay bekleyen',
          value: this.formatNumber(this.queue.totalCount),
          helper: 'Süresi dolmuş uygun kayıt',
        },
        {
          label: 'Toplam tutar',
          value: this.formatMoney(this.queue.totalAmount),
          helper: 'Henüz kesinleşmiş bakiye değil',
        },
        {
          label: 'Hakediş',
          value: this.formatMoney(this.queue.earningAmount),
          helper: 'Influencer kazançları',
        },
        {
          label: 'Nakit iade',
          value: this.formatMoney(this.queue.cashbackAmount),
          helper: 'Kullanıcı alışveriş iadeleri',
        },
      ]
    },
  },
  mounted() {
    this.refreshAll()
  },
  beforeDestroy() {
    this.cancellationRequestId += 1
  },
  methods: {
    settlementUrl(path) {
      return `${process.env.VUE_APP_BASE_API_URL}admin/wallet-settlements${path}`
    },
    async refreshAll() {
      await Promise.all([this.fetchQueue(), this.fetchHistory(), this.fetchUpdates(), this.fetchCancellations()])
    },
    updateCancellationOptions(options) {
      const { itemsPerPage } = options
      const page = itemsPerPage !== this.cancellationOptions.itemsPerPage ? 1 : options.page
      if (page === this.cancellationOptions.page && itemsPerPage === this.cancellationOptions.itemsPerPage) return
      this.cancellationOptions = { page, itemsPerPage }
      this.fetchCancellations()
    },
    searchCancellations() {
      this.appliedCancellationSearch = this.cancellationSearch || ''
      this.cancellationOptions = { ...this.cancellationOptions, page: 1 }
      this.fetchCancellations()
    },
    clearCancellationSearch() {
      this.cancellationSearch = ''
      this.searchCancellations()
    },
    async fetchCancellations() {
      this.cancellationRequestId += 1
      const requestId = this.cancellationRequestId
      this.cancellationsLoading = true
      this.cancellationsError = ''
      try {
        const response = await axios.get(this.settlementUrl('/cancellations'), {
          ...this.requestOptions,
          params: {
            page: this.cancellationOptions.page - 1,
            size: this.cancellationOptions.itemsPerPage,
            search: this.appliedCancellationSearch,
          },
        })
        if (requestId !== this.cancellationRequestId) return
        this.cancellations = response?.data?.data || { content: [], totalElements: 0 }
        if (this.cancellationOptions.page > Math.max(1, this.cancellations.totalPages || 0)) {
          this.cancellationOptions = { ...this.cancellationOptions, page: Math.max(1, this.cancellations.totalPages || 0) }
          this.fetchCancellations()
        }
      } catch (error) {
        if (requestId !== this.cancellationRequestId) return
        this.cancellations = { content: [], totalElements: 0 }
        this.cancellationsError = this.resolveError(error, 'İptal geçmişi yüklenemedi.')
      } finally {
        if (requestId === this.cancellationRequestId) this.cancellationsLoading = false
      }
    },
    cancellationReasonLabel(reason) {
      return {
        NETWORK_REJECTED: 'Network tarafından reddedildi',
        NETWORK_MISSING: 'Network kaydı bulunamadı',
        AMOUNT_ZERO: 'Kazanç sıfırlandı',
        NETWORK_STATUS_UPDATED: 'Network durumu değişti',
        NETWORK_OBSERVED: 'İlk network kaydı iptal',
      }[reason] || reason || 'İptal'
    },
    async fetchQueue() {
      this.loading = true
      this.errorMessage = ''
      try {
        const params = {}
        if (this.filters.search) params.search = this.filters.search
        if (this.filters.networkSource) params.networkSource = this.filters.networkSource
        if (this.filters.type) params.type = this.filters.type
        const response = await axios.get(this.settlementUrl('/queue'), {
          ...this.requestOptions,
          params,
        })
        this.queue = response?.data?.data || this.queue
        this.selectedItems = []
      } catch (error) {
        this.errorMessage = this.resolveError(error, 'Mutabakat listesi yüklenemedi.')
      } finally {
        this.loading = false
      }
    },
    async fetchHistory() {
      this.historyLoading = true
      try {
        const response = await axios.get(this.settlementUrl('/history'), this.requestOptions)
        this.history = response?.data?.data || this.history
      } catch (error) {
        this.errorMessage = this.resolveError(error, 'Mutabakat geçmişi yüklenemedi.')
      } finally {
        this.historyLoading = false
      }
    },
    async fetchUpdates() {
      this.updatesLoading = true
      try {
        const response = await axios.get(this.settlementUrl('/updates'), this.requestOptions)
        this.updates = response?.data?.data || this.updates
      } catch (error) {
        this.errorMessage = this.resolveError(error, 'Tutar güncellemeleri yüklenemedi.')
      } finally {
        this.updatesLoading = false
      }
    },
    async prepareConfirmation() {
      if (!this.selectedItems.length) return
      this.previewLoading = true
      this.errorMessage = ''
      try {
        const response = await axios.post(
          this.settlementUrl('/preview'),
          { entryIds: this.selectedItems.map(item => item.id) },
          this.requestOptions,
        )
        this.previewPlan = response?.data?.data || null
        this.approvalNote = ''
        this.confirmDialog = !!this.previewPlan
      } catch (error) {
        this.errorMessage = this.resolveError(error, 'Mutabakat önizlemesi hazırlanamadı.')
        await this.fetchQueue()
      } finally {
        this.previewLoading = false
      }
    },
    async confirmSettlement() {
      this.confirmLoading = true
      this.errorMessage = ''
      try {
        const response = await axios.post(
          this.settlementUrl('/confirm'),
          {
            entryIds: this.previewPlan.items.map(item => item.id),
            planHash: this.previewPlan.planHash,
            note: this.approvalNote || null,
          },
          this.requestOptions,
        )
        const result = response?.data?.data || {}
        this.successMessage = `${this.formatNumber(result.confirmedCount)} kayıt, ${this.formatMoney(result.confirmedAmount)} kesinleştirildi.`
        this.closeConfirmation()
        await this.refreshAll()
      } catch (error) {
        this.errorMessage = this.resolveError(error, 'Mutabakat kesinleştirilemedi.')
        this.confirmDialog = false
        await this.fetchQueue()
      } finally {
        this.confirmLoading = false
      }
    },
    closeConfirmation() {
      this.confirmDialog = false
      this.previewPlan = null
      this.approvalNote = ''
    },
    resolveError(error, fallback) {
      return error?.response?.data?.message
        || error?.response?.data?.error
        || fallback
    },
    typeLabel(type) {
      return type === 'CASHBACK' ? 'Nakit iade' : 'Hakediş'
    },
    updateReasonLabel(reason) {
      if (reason === 'ADMIN_APPROVED_NETWORK_CHANGED') {
        return 'Onay sonrası network farkı'
      }

      return 'Tutar güncellemesi'
    },
    updateReasonColor(reason) {
      return reason === 'ADMIN_APPROVED_NETWORK_CHANGED' ? 'warning' : 'primary'
    },
    statusLabel(status) {
      const labels = {
        EXPECTED: 'Bekleyen',
        CONFIRMED: 'Kesinleşen',
        CANCELLED: 'İptal',
      }

      return labels[status] || status || '-'
    },
    amountDeltaColor(value) {
      const amount = Number(value || 0)
      if (amount > 0) return 'success'
      if (amount < 0) return 'error'

      return 'secondary'
    },
    formatSignedMoney(value) {
      const amount = Number(value || 0)
      if (amount > 0) return `+${this.formatMoney(amount)}`
      if (amount < 0) return `-${this.formatMoney(Math.abs(amount))}`

      return this.formatMoney(0)
    },
    formatMoney(value) {
      return `₺${new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value || 0))}`
    },
    formatNumber(value) {
      return new Intl.NumberFormat('tr-TR').format(Number(value || 0))
    },
    formatDateTime(value) {
      if (!value) return '-'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return '-'

      return parsed.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    conversionDateRange(items) {
      const timestamps = (items || [])
        .map(item => (item.entryDate ? new Date(item.entryDate).getTime() : Number.NaN))
        .filter(value => Number.isFinite(value))
      if (!timestamps.length) return '-'
      const first = new Date(Math.min(...timestamps))
      const last = new Date(Math.max(...timestamps))

      return first.getTime() === last.getTime()
        ? this.formatDateTime(first)
        : `${this.formatDateTime(first)} – ${this.formatDateTime(last)}`
    },
  },
}
</script>

<style scoped>
.settlement-approval__filters {
  padding-bottom: 12px;
}

.settlement-approval__selection-bar {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0 16px 16px;
}

.settlement-approval__identifier {
  display: flex;
  flex-direction: column;
  max-width: 210px;
  word-break: break-all;
}

.settlement-approval__identifier small {
  color: var(--v-text-secondary-base);
  margin-top: 3px;
}

.settlement-approval__preview-facts {
  display: grid;
  gap: 12px;
  margin: 0;
}

.settlement-approval__preview-facts div {
  background: rgba(127, 127, 127, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
}

.settlement-approval__preview-facts dt {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
}

.settlement-approval__preview-facts dd {
  font-weight: 600;
  margin: 4px 0 0;
}

.settlement-approval__hash {
  font-family: monospace;
  font-size: 11px;
  overflow-wrap: anywhere;
}

@media (max-width: 600px) {
  .settlement-approval__selection-bar {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
